import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, TrendingDown, Package, Truck, Settings, Star, AlertTriangle, Search, Bell, Archive, ShoppingCart, RefreshCw, ArrowRight, Check, FileText } from 'lucide-react';

const SupplierIntelligencePage = () => {
  const [articles, setArticles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [supplierStock, setSupplierStock] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [varianceSettings, setVarianceSettings] = useState({ threshold_percent: 5, require_approval_above: 10, enabled: true });
  const [showSettings, setShowSettings] = useState(false);
  const [tab, setTab] = useState('comparatie');
  const [savingsReport, setSavingsReport] = useState(null);
  const [savingsLoading, setSavingsLoading] = useState(false);
  const [savingsPeriod, setSavingsPeriod] = useState(6);
  // Smart Alerts state
  const [smartAlerts, setSmartAlerts] = useState(null);
  const [smartAlertsLoading, setSmartAlertsLoading] = useState(false);
  // Dead Stock state
  const [deadStock, setDeadStock] = useState(null);
  const [deadStockLoading, setDeadStockLoading] = useState(false);
  const [deadStockDays, setDeadStockDays] = useState(180);
  // Generate Order state
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderSelectedIds, setOrderSelectedIds] = useState([]);
  const [orderLocations, setOrderLocations] = useState([]);
  const [orderLocationId, setOrderLocationId] = useState('');
  const [orderImportance, setOrderImportance] = useState('high');
  const [orderNotes, setOrderNotes] = useState('');
  const [orderSubmitting, setOrderSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [articlesRes, suppliersRes, settingsRes] = await Promise.all([
        api.get('/articles'),
        api.get('/suppliers'),
        api.get('/supplier-intelligence/settings/price-variance')
      ]);
      setArticles(articlesRes.data.filter(a => a.suppliers?.length > 0));
      setSuppliers(suppliersRes.data);
      setVarianceSettings(settingsRes.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const selectArticle = async (article) => {
    setSelectedArticle(article);
    try {
      const [stockRes, historyRes] = await Promise.all([
        api.get(`/supplier-intelligence/articles/${article.id}/supplier-stock`),
        api.get(`/supplier-intelligence/articles/${article.id}/price-history`)
      ]);
      setSupplierStock(stockRes.data);
      setPriceHistory(historyRes.data.price_history || []);
    } catch {
      toast.error('Eroare la incarcarea datelor furnizor');
    }
  };

  const saveVarianceSettings = async () => {
    try {
      await api.put('/supplier-intelligence/settings/price-variance', varianceSettings);
      toast.success('Setari salvate!');
      setShowSettings(false);
    } catch {
      toast.error('Eroare la salvare');
    }
  };

  const fetchSavingsReport = async (period) => {
    try {
      setSavingsLoading(true);
      const res = await api.get(`/supplier-intelligence/savings-report?months=${period || savingsPeriod}`);
      setSavingsReport(res.data);
    } catch {
      toast.error('Eroare la incarcarea raportului');
    } finally {
      setSavingsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'economii' && !savingsReport) {
      fetchSavingsReport();
    }
    if (tab === 'alerte' && !smartAlerts) {
      fetchSmartAlerts();
    }
    if (tab === 'stoc-mort' && !deadStock) {
      fetchDeadStock();
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSmartAlerts = async () => {
    try {
      setSmartAlertsLoading(true);
      const res = await api.get('/supplier-intelligence/smart-alerts');
      setSmartAlerts(res.data);
    } catch {
      toast.error('Eroare la incarcarea alertelor');
    } finally {
      setSmartAlertsLoading(false);
    }
  };

  const fetchDeadStock = async (d) => {
    try {
      setDeadStockLoading(true);
      const res = await api.get(`/supplier-intelligence/dead-stock?days=${d || deadStockDays}`);
      setDeadStock(res.data);
    } catch {
      toast.error('Eroare la incarcarea raportului stoc mort');
    } finally {
      setDeadStockLoading(false);
    }
  };

  // ===== Generate Order from Smart Alerts =====
  const openOrderDialog = async () => {
    // Pre-select all alerts
    if (smartAlerts?.alerts) {
      setOrderSelectedIds(smartAlerts.alerts.map((_, i) => i));
    }
    // Fetch locations for the dropdown
    try {
      const res = await api.get('/locations');
      setOrderLocations(res.data || []);
    } catch {
      setOrderLocations([]);
    }
    setOrderLocationId('');
    setOrderImportance('high');
    setOrderNotes('Comanda generata automat din Alerte Inteligente');
    setShowOrderDialog(true);
  };

  const toggleOrderItem = (idx) => {
    setOrderSelectedIds(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const submitOrder = async () => {
    if (!orderLocationId) {
      toast.error('Selectati o locatie');
      return;
    }
    if (orderSelectedIds.length === 0) {
      toast.error('Selectati cel putin un articol');
      return;
    }

    const items = orderSelectedIds.map(idx => {
      const alert = smartAlerts.alerts[idx];
      const price = alert.best_supplier?.price || 0;
      return {
        article_id: alert.article_id,
        article_name: alert.article_name,
        article_code: alert.article_code,
        unit: alert.unit,
        quantity: alert.deficit,
        unit_price: price,
      };
    });

    try {
      setOrderSubmitting(true);
      await api.post('/material-requests', {
        location_id: orderLocationId,
        importance: orderImportance,
        notes: orderNotes,
        items,
      });
      toast.success(`Comanda creata cu ${items.length} articole!`);
      setShowOrderDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la crearea comenzii');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const filteredArticles = articles.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (a.name?.toLowerCase().includes(q) || a.code?.toLowerCase().includes(q)) return true;
    return a.suppliers?.some(s => 
      s.supplier_article_name?.toLowerCase().includes(q) || 
      s.supplier_name?.toLowerCase().includes(q)
    );
  });

  // Group price history by supplier
  const priceBySupplier = {};
  priceHistory.forEach(entry => {
    const sid = entry.supplier_id || 'unknown';
    if (!priceBySupplier[sid]) priceBySupplier[sid] = { name: entry.supplier_name || 'Necunoscut', entries: [] };
    priceBySupplier[sid].entries.push(entry);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-intelligence-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Comparatie Furnizori</h1>
          <p className="text-slate-600 mt-1">{articles.length} articole cu furnizori configurati</p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="variance-settings-btn">
          <Settings className="w-4 h-4 mr-2" />
          Prag Variatie: {varianceSettings.threshold_percent}%
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-lg w-fit flex-wrap">
        {[
          { id: 'comparatie', label: 'Comparatie Preturi', icon: BarChart3 },
          { id: 'stoc', label: 'Stoc per Furnizor', icon: Package },
          { id: 'economii', label: 'Economii Potentiale', icon: TrendingDown },
          { id: 'alerte', label: 'Alerte Inteligente', icon: Bell },
          { id: 'stoc-mort', label: 'Stoc Mort', icon: Archive },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cauta articol (intern sau denumire furnizor)..."
          className="pl-10 h-11"
          data-testid="search-articles"
        />
      </div>

      {tab === 'comparatie' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Articles List */}
          <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto">
            {filteredArticles.map(article => {
              const minPrice = Math.min(...(article.suppliers || []).filter(s => s.price > 0).map(s => s.price));
              const maxPrice = Math.max(...(article.suppliers || []).filter(s => s.price > 0).map(s => s.price));
              const spread = maxPrice - minPrice;
              
              return (
                <div
                  key={article.id}
                  onClick={() => selectArticle(article)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedArticle?.id === article.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`article-item-${article.code}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{article.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{article.code}</Badge>
                        <span className="text-xs text-slate-500">{article.suppliers?.length || 0} furnizori</span>
                      </div>
                    </div>
                    {spread > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Diferenta</p>
                        <p className="text-sm font-bold text-orange-600">{spread.toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredArticles.length === 0 && (
              <div className="text-center py-8 text-slate-500">Niciun articol gasit</div>
            )}
          </div>

          {/* Article Detail */}
          <div className="lg:col-span-2 space-y-4">
            {selectedArticle ? (
              <>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <span>{selectedArticle.name}</span>
                      <Badge>{selectedArticle.code}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Supplier comparison cards */}
                    {(selectedArticle.suppliers || []).map((supplier, idx) => {
                      const allPrices = (selectedArticle.suppliers || []).filter(s => s.price > 0).map(s => s.price);
                      const isCheapest = allPrices.length > 1 && supplier.price > 0 && supplier.price === Math.min(...allPrices);
                      const isMostExpensive = allPrices.length > 1 && supplier.price > 0 && supplier.price === Math.max(...allPrices) && !isCheapest;
                      const maxP = Math.max(...allPrices, 1);

                      return (
                        <div key={idx} className={`p-4 rounded-lg border-2 ${
                          supplier.is_preferred ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'
                        }`} data-testid={`supplier-compare-${idx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {supplier.is_preferred && <Star className="w-4 h-4 text-blue-600 fill-blue-600" />}
                              <span className="font-bold">{supplier.supplier_name}</span>
                              {supplier.is_preferred && <Badge className="bg-blue-600 text-white text-xs">PREFERAT</Badge>}
                              {isCheapest && <Badge className="bg-emerald-600 text-white text-xs">CEL MAI IEFTIN</Badge>}
                              {isMostExpensive && <Badge variant="outline" className="border-orange-300 text-orange-600 text-xs">CEL MAI SCUMP</Badge>}
                            </div>
                            <span className="text-2xl font-black font-mono">{supplier.price > 0 ? `${supplier.price.toFixed(2)}` : '-'}</span>
                          </div>
                          {supplier.supplier_article_name && (
                            <p className="text-sm text-slate-600 mb-2">Denumire furnizor: <strong>{supplier.supplier_article_name}</strong></p>
                          )}
                          {supplier.price > 0 && allPrices.length > 1 && (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${isCheapest ? 'bg-emerald-500' : isMostExpensive ? 'bg-orange-400' : 'bg-blue-400'}`}
                                  style={{ width: `${(supplier.price / maxP) * 100}%` }}
                                />
                              </div>
                              <span className="text-xs font-mono text-slate-600 w-16 text-right">{supplier.price.toFixed(2)} RON</span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Savings potential */}
                    {(() => {
                      const prices = (selectedArticle.suppliers || []).filter(s => s.price > 0).map(s => s.price);
                      if (prices.length < 2) return null;
                      const min = Math.min(...prices);
                      const max = Math.max(...prices);
                      const diff = max - min;
                      const pctDiff = ((diff / max) * 100).toFixed(1);
                      return (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-2">
                          <div className="flex items-center gap-2">
                            <TrendingDown className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-800">
                              Economie potentiala: {diff.toFixed(2)} RON/buc ({pctDiff}%) alegand cel mai ieftin furnizor
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Price History */}
                {Object.keys(priceBySupplier).length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-500" />
                        Istoric Preturi (din receptii)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(priceBySupplier).map(([sid, data]) => (
                          <div key={sid} className="border rounded-lg p-3">
                            <p className="font-medium text-sm mb-2">{data.name}</p>
                            <div className="space-y-1">
                              {data.entries.slice(0, 5).map((entry, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs">
                                  <span className="text-slate-500">
                                    {entry.recorded_at ? new Date(entry.recorded_at).toLocaleDateString('ro-RO') : entry.created_at ? new Date(entry.created_at).toLocaleDateString('ro-RO') : '-'}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-bold">{(entry.price || entry.unit_price || 0).toFixed(2)} RON</span>
                                    <span className="text-slate-400">x{entry.quantity}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Stock per supplier */}
                {supplierStock && supplierStock.suppliers?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Package className="w-4 h-4 text-blue-500" />
                        Stoc per Furnizor (Total: {supplierStock.total_stock} {supplierStock.unit})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {supplierStock.suppliers.map((s, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className="text-sm font-medium w-32 truncate">{s.supplier_name}</span>
                            <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${s.percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold w-20 text-right">{s.total_quantity} ({s.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-slate-500">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="font-medium">Selectati un articol din lista</p>
                  <p className="text-sm mt-1">pentru a vedea comparatia furnizorilor</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'stoc' && (
        <div className="space-y-3">
          {filteredArticles.map(article => {
            const articleSuppliers = article.suppliers || [];
            if (articleSuppliers.length === 0) return null;
            return (
              <Card key={article.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-bold">{article.name}</p>
                      <p className="text-xs text-slate-500">{article.code} | Stoc: {article.total_stock} {article.unit}</p>
                    </div>
                    <Badge variant="outline">{articleSuppliers.length} furnizori</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {articleSuppliers.map((s, idx) => (
                      <div key={idx} className={`p-2 rounded border text-sm ${s.is_preferred ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-1">
                          {s.is_preferred && <Star className="w-3 h-3 text-blue-600 fill-blue-600" />}
                          <span className="font-medium text-xs">{s.supplier_name}</span>
                        </div>
                        {s.supplier_article_name && <p className="text-xs text-slate-500 mt-0.5 truncate">{s.supplier_article_name}</p>}
                        <p className="text-xs font-mono mt-1">{s.price > 0 ? `${s.price.toFixed(2)} RON` : '-'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'economii' && (
        <div className="space-y-6" data-testid="savings-tab">
          {/* Period selector */}
          <div className="flex items-center gap-3">
            <Label className="text-sm text-slate-600 whitespace-nowrap">Perioada analiza:</Label>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              {[3, 6, 12, 24].map(p => (
                <button key={p}
                  onClick={() => { setSavingsPeriod(p); setSavingsReport(null); fetchSavingsReport(p); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    savingsPeriod === p ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`period-${p}`}>
                  {p} luni
                </button>
              ))}
            </div>
          </div>

          {savingsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : savingsReport ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-2 border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-500 mb-1">Total Achizitii</p>
                    <p className="text-2xl font-black text-slate-900 font-mono">{savingsReport.total_spent.toLocaleString('ro-RO', {minimumFractionDigits: 2})} <span className="text-sm font-normal text-slate-500">RON</span></p>
                    <p className="text-xs text-slate-400 mt-1">ultimele {savingsReport.period_months} luni</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-emerald-200 bg-emerald-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-emerald-700 mb-1">Cost Optim (cel mai ieftin)</p>
                    <p className="text-2xl font-black text-emerald-800 font-mono">{savingsReport.optimal_cost.toLocaleString('ro-RO', {minimumFractionDigits: 2})} <span className="text-sm font-normal text-emerald-600">RON</span></p>
                    <p className="text-xs text-emerald-500 mt-1">daca se cumpara la cel mai mic pret</p>
                  </CardContent>
                </Card>
                <Card className={`border-2 ${savingsReport.total_savings > 0 ? 'border-orange-300 bg-orange-50/30' : 'border-green-300 bg-green-50/30'}`}>
                  <CardContent className="p-5">
                    <p className="text-sm text-orange-700 mb-1">Economie Potentiala</p>
                    <p className="text-2xl font-black text-orange-800 font-mono">{savingsReport.total_savings.toLocaleString('ro-RO', {minimumFractionDigits: 2})} <span className="text-sm font-normal text-orange-600">RON</span></p>
                    <p className="text-xs text-orange-500 mt-1">{savingsReport.savings_percent}% din total</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-200">
                  <CardContent className="p-5">
                    <p className="text-sm text-blue-600 mb-1">Articole cu Potential</p>
                    <p className="text-2xl font-black text-blue-800">{savingsReport.articles_with_savings || 0}</p>
                    <p className="text-xs text-blue-400 mt-1">articole cu economii posibile</p>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Trend */}
              {savingsReport.monthly_trend?.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      Trend Lunar - Cheltuieli vs. Cost Optim
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {savingsReport.monthly_trend.map((m, idx) => {
                        const maxVal = Math.max(...savingsReport.monthly_trend.map(t => t.spent), 1);
                        return (
                          <div key={idx} className="flex items-center gap-3" data-testid={`month-row-${m.month}`}>
                            <span className="text-xs font-mono text-slate-600 w-16">{m.month}</span>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-slate-400 rounded-full" style={{ width: `${(m.spent / maxVal) * 100}%` }} />
                                </div>
                                <span className="text-xs font-mono w-20 text-right text-slate-600">{m.spent.toFixed(0)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-3 bg-emerald-50 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${(m.optimal / maxVal) * 100}%` }} />
                                </div>
                                <span className="text-xs font-mono w-20 text-right text-emerald-600">{m.optimal.toFixed(0)}</span>
                              </div>
                            </div>
                            {m.savings > 0 && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs font-mono">{m.savings.toFixed(0)}</Badge>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex items-center gap-4 pt-2 text-xs text-slate-500">
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-slate-400 rounded"></div> Cheltuieli reale</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-400 rounded"></div> Cost optim</div>
                        <div className="flex items-center gap-1"><Badge className="bg-orange-100 text-orange-700 text-xs py-0 px-1">N</Badge> Economie posibila</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top savings articles */}
              {savingsReport.articles?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-emerald-500" />
                      Detalierea Economiilor Potentiale ({savingsReport.articles.length} articole)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {savingsReport.articles.map((a, idx) => (
                        <div key={idx} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors" data-testid={`savings-article-${idx}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-sm">{a.article_name}</p>
                                <Badge variant="outline" className="text-xs font-mono">{a.article_code}</Badge>
                                <Badge variant="outline" className="text-xs">{a.suppliers_count} furnizori</Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span>Cantitate: <strong>{a.total_quantity} {a.unit}</strong></span>
                                <span>Platit: <strong className="text-slate-800 font-mono">{a.total_spent.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</strong></span>
                                <span>Optim: <strong className="text-emerald-700 font-mono">{a.optimal_cost.toLocaleString('ro-RO', {minimumFractionDigits: 2})} RON</strong></span>
                              </div>
                              <p className="text-xs text-emerald-600 mt-1">
                                Cel mai ieftin: <strong>{a.cheapest_supplier}</strong> la {a.cheapest_price.toFixed(2)} RON/{a.unit}
                              </p>
                              {/* Show purchase details */}
                              {a.purchases?.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {a.purchases.map((p, pidx) => (
                                    <div key={pidx} className="flex items-center gap-2 text-xs text-slate-400">
                                      <span>{p.date}</span>
                                      <span>{p.supplier_name}:</span>
                                      <span className="font-mono">{p.price_paid.toFixed(2)} x {p.quantity}</span>
                                      <span className="text-orange-600 font-medium">+{p.overpaid.toFixed(2)} RON</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xl font-black text-orange-600 font-mono">{a.saving.toLocaleString('ro-RO', {minimumFractionDigits: 2})}</p>
                              <p className="text-xs text-orange-500">RON ({a.saving_percent}%)</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <TrendingDown className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
                    <p className="font-medium text-emerald-800 text-lg">Felicitari! Nicio economie de realizat.</p>
                    <p className="text-sm text-slate-500 mt-1">Achizitiile s-au facut la cel mai bun pret disponibil in aceasta perioada.</p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* =============== TAB: ALERTE INTELIGENTE (P3) =============== */}
      {tab === 'alerte' && (
        <div className="space-y-6" data-testid="smart-alerts-tab">
          {smartAlertsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div>
            </div>
          ) : smartAlerts ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-2 border-red-200 bg-red-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-red-600 mb-1">Critice (stoc 0)</p>
                    <p className="text-3xl font-black text-red-700">{smartAlerts.critical}</p>
                    <p className="text-xs text-red-400 mt-1">necesita comanda urgenta</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-amber-200 bg-amber-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-amber-600 mb-1">Atentionare</p>
                    <p className="text-3xl font-black text-amber-700">{smartAlerts.warning}</p>
                    <p className="text-xs text-amber-400 mt-1">sub stoc minim</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-500 mb-1">Total Alerte</p>
                    <p className="text-3xl font-black text-slate-800">{smartAlerts.total}</p>
                    <p className="text-xs text-slate-400 mt-1">articole sub minim</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-blue-200 bg-blue-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-blue-600 mb-1">Cost Estimat Reaprovizionare</p>
                    <p className="text-2xl font-black text-blue-800 font-mono">{(smartAlerts.total_reorder_cost || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-blue-400 mt-1">RON (la cel mai bun pret)</p>
                  </CardContent>
                </Card>
              </div>

              {/* Generate Order Button */}
              {smartAlerts.alerts?.length > 0 && (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div>
                    <p className="font-semibold text-emerald-800 flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Genereaza comanda de aprovizionare
                    </p>
                    <p className="text-sm text-emerald-600 mt-0.5">
                      Creeaza automat o cerere cu {smartAlerts.alerts.length} articole sub stoc minim
                    </p>
                  </div>
                  <Button onClick={openOrderDialog} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-order-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Genereaza Comanda
                  </Button>
                </div>
              )}

              {/* Alerts List */}
              {smartAlerts.alerts?.length > 0 ? (
                <div className="space-y-3">
                  {smartAlerts.alerts.map((alert, idx) => (
                    <Card key={idx} className={`border-l-4 ${alert.severity === 'critical' ? 'border-l-red-500' : 'border-l-amber-400'}`} data-testid={`smart-alert-${idx}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              {alert.severity === 'critical' ? (
                                <Badge className="bg-red-600 text-white text-xs">CRITIC</Badge>
                              ) : (
                                <Badge className="bg-amber-500 text-white text-xs">ATENTIE</Badge>
                              )}
                              <span className="font-bold text-sm">{alert.article_name}</span>
                              <Badge variant="outline" className="text-xs font-mono">{alert.article_code}</Badge>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-slate-600 mb-3">
                              <span>Stoc: <strong className={alert.current_stock === 0 ? 'text-red-600' : 'text-amber-600'}>{alert.current_stock}</strong> / {alert.min_stock} {alert.unit}</span>
                              <span>Deficit: <strong className="text-red-600">{alert.deficit} {alert.unit}</strong></span>
                            </div>

                            {/* Best supplier recommendation */}
                            {alert.best_supplier && (
                              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <ShoppingCart className="w-4 h-4 text-emerald-600" />
                                  <span className="text-sm font-semibold text-emerald-800">Recomandare comanda</span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-emerald-700 flex-wrap">
                                  <span>Furnizor: <strong>{alert.best_supplier.supplier_name}</strong></span>
                                  {alert.best_supplier.price > 0 && (
                                    <span>Pret: <strong className="font-mono">{alert.best_supplier.price.toFixed(2)} RON</strong></span>
                                  )}
                                  {alert.best_supplier.lead_time_days > 0 && (
                                    <span>Termen: <strong>{alert.best_supplier.lead_time_days} zile</strong></span>
                                  )}
                                  {alert.best_supplier.estimated_cost && (
                                    <span>Cost total: <strong className="font-mono">{alert.best_supplier.estimated_cost.toFixed(2)} RON</strong></span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Alternative suppliers */}
                            {alert.all_suppliers?.length > 1 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {alert.all_suppliers.slice(1, 4).map((s, si) => (
                                  <div key={si} className="bg-slate-50 border rounded px-2 py-1 text-xs">
                                    <span className="text-slate-600">{s.supplier_name}</span>
                                    {s.price > 0 && <span className="ml-1 font-mono font-bold">{s.price.toFixed(2)}</span>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Substitutes available */}
                            {alert.substitutes_available?.length > 0 && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-1">
                                <p className="text-xs font-semibold text-blue-700 mb-1">Articole substitut disponibile:</p>
                                <div className="flex flex-wrap gap-2">
                                  {alert.substitutes_available.map((sub, si) => (
                                    <div key={si} className="flex items-center gap-1 bg-white border border-blue-200 rounded px-2 py-1 text-xs">
                                      <Package className="w-3 h-3 text-blue-500" />
                                      <span className="font-medium">{sub.name}</span>
                                      <Badge variant="outline" className="text-[10px] py-0 px-1">{sub.code}</Badge>
                                      <span className="text-emerald-600 font-bold">stoc: {sub.stock}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="text-right shrink-0">
                            <p className="text-xs text-slate-400">{alert.suppliers_count} furnizori</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Bell className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
                    <p className="font-medium text-emerald-800 text-lg">Totul in regula!</p>
                    <p className="text-sm text-slate-500 mt-1">Niciun articol nu este sub stocul minim configurat.</p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* =============== TAB: STOC MORT (P4) =============== */}
      {tab === 'stoc-mort' && (
        <div className="space-y-6" data-testid="dead-stock-tab">
          {/* Period selector */}
          <div className="flex items-center gap-3">
            <Label className="text-sm text-slate-600 whitespace-nowrap">Fara consum de:</Label>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              {[90, 180, 365, 730].map(d => (
                <button key={d}
                  onClick={() => { setDeadStockDays(d); setDeadStock(null); fetchDeadStock(d); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    deadStockDays === d ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`deadstock-period-${d}`}>
                  {d < 365 ? `${d} zile` : `${d / 365} ani`}
                </button>
              ))}
            </div>
          </div>

          {deadStockLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-600"></div>
            </div>
          ) : deadStock ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-2 border-orange-200 bg-orange-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-orange-600 mb-1">Articole Stagnante</p>
                    <p className="text-3xl font-black text-orange-700">{deadStock.total_items}</p>
                    <p className="text-xs text-orange-400 mt-1">fara miscare &gt; {deadStock.days_threshold} zile</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-red-200 bg-red-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-red-600 mb-1">Capital Blocat</p>
                    <p className="text-2xl font-black text-red-700 font-mono">{deadStock.total_blocked_value.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-red-400 mt-1">RON valoare estimata</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-slate-200">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-500 mb-1">Cantitate Totala</p>
                    <p className="text-3xl font-black text-slate-800">{deadStock.total_blocked_qty}</p>
                    <p className="text-xs text-slate-400 mt-1">unitati stagnante</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-purple-200 bg-purple-50/30">
                  <CardContent className="p-5">
                    <p className="text-sm text-purple-600 mb-1">Niciodata Consumate</p>
                    <p className="text-3xl font-black text-purple-700">{deadStock.never_consumed_count}</p>
                    <p className="text-xs text-purple-400 mt-1">receptionate dar nefolosite</p>
                  </CardContent>
                </Card>
              </div>

              {/* Dead Stock Items */}
              {deadStock.dead_stock_items?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Archive className="w-4 h-4 text-orange-500" />
                      Detalierea Stocului Mort ({deadStock.dead_stock_items.length} articole)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="dead-stock-table">
                        <thead>
                          <tr className="bg-slate-50 border-b">
                            <th className="p-3 text-left text-xs font-semibold text-slate-500">Articol</th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-500">Cod</th>
                            <th className="p-3 text-right text-xs font-semibold text-slate-500">Cantitate</th>
                            <th className="p-3 text-right text-xs font-semibold text-slate-500">Valoare Blocata</th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-500">Ultima Utilizare</th>
                            <th className="p-3 text-left text-xs font-semibold text-slate-500">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deadStock.dead_stock_items.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50 transition-colors" data-testid={`dead-stock-row-${idx}`}>
                              <td className="p-3">
                                <p className="font-medium text-sm">{item.article_name}</p>
                              </td>
                              <td className="p-3">
                                <Badge variant="outline" className="text-xs font-mono">{item.article_code}</Badge>
                              </td>
                              <td className="p-3 text-right font-mono font-bold">{item.quantity} {item.unit}</td>
                              <td className="p-3 text-right">
                                <span className={`font-mono font-bold ${item.blocked_value > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                  {item.blocked_value > 0 ? `${item.blocked_value.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} RON` : '-'}
                                </span>
                              </td>
                              <td className="p-3">
                                {item.never_consumed ? (
                                  <span className="text-purple-600 text-xs font-medium">Niciodata</span>
                                ) : item.last_consumed_at ? (
                                  <div>
                                    <span className="text-xs text-slate-600">{new Date(item.last_consumed_at).toLocaleDateString('ro-RO')}</span>
                                    {item.days_since_consumption && (
                                      <span className="text-xs text-slate-400 ml-1">({item.days_since_consumption} zile)</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-xs">-</span>
                                )}
                              </td>
                              <td className="p-3">
                                {item.never_consumed ? (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">Neutilizat</Badge>
                                ) : item.days_since_consumption && item.days_since_consumption > 365 ? (
                                  <Badge className="bg-red-100 text-red-700 text-xs">Blocat &gt; 1 an</Badge>
                                ) : (
                                  <Badge className="bg-orange-100 text-orange-700 text-xs">Stagnant</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Archive className="w-12 h-12 mx-auto text-emerald-300 mb-3" />
                    <p className="font-medium text-emerald-800 text-lg">Excelent! Niciun stoc mort.</p>
                    <p className="text-sm text-slate-500 mt-1">Toate articolele din stoc au fost consumate in ultimele {deadStock.days_threshold} zile.</p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Variance Settings Dialog */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Setari Variatie Pret
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Prag alerta variatie (%)</Label>
                <Input
                  type="number" min="1" max="50" step="1"
                  value={varianceSettings.threshold_percent}
                  onChange={(e) => setVarianceSettings({...varianceSettings, threshold_percent: parseFloat(e.target.value) || 5})}
                  className="h-11"
                  data-testid="variance-threshold-input"
                />
                <p className="text-xs text-slate-500">La receptie, daca pretul difera cu mai mult de acest procent, se va afisa o alerta</p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowSettings(false)}>Anuleaza</Button>
                <Button className="flex-1" onClick={saveVarianceSettings} data-testid="save-variance-settings">Salveaza</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-emerald-600" />
              Genereaza Comanda de Aprovizionare
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Location + Importance */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Locatie *</Label>
                <Select value={orderLocationId} onValueChange={setOrderLocationId}>
                  <SelectTrigger className="h-11" data-testid="order-location-select">
                    <SelectValue placeholder="Selectati locatia" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {orderLocations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name} {loc.plant_name ? `(${loc.plant_name})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Urgenta</Label>
                <Select value={orderImportance} onValueChange={setOrderImportance}>
                  <SelectTrigger className="h-11" data-testid="order-importance-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critica</SelectItem>
                    <SelectItem value="high">Ridicata</SelectItem>
                    <SelectItem value="medium">Medie</SelectItem>
                    <SelectItem value="low">Scazuta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                rows={2}
                className="text-sm"
                data-testid="order-notes-input"
              />
            </div>

            <Separator />

            {/* Items selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Articole ({orderSelectedIds.length} / {smartAlerts?.alerts?.length || 0} selectate)</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setOrderSelectedIds(smartAlerts?.alerts?.map((_, i) => i) || [])}
                    className="text-xs" data-testid="select-all-order-items">Selecteaza tot</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setOrderSelectedIds([])}
                    className="text-xs">Deselecteaza</Button>
                </div>
              </div>

              <div className="max-h-[40vh] overflow-y-auto space-y-1 border rounded-lg p-2">
                {smartAlerts?.alerts?.map((alert, idx) => {
                  const selected = orderSelectedIds.includes(idx);
                  return (
                    <div key={idx}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${selected ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50 border border-transparent'}`}
                      onClick={() => toggleOrderItem(idx)}
                      data-testid={`order-item-${idx}`}
                    >
                      <Checkbox checked={selected} className="pointer-events-none" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {alert.severity === 'critical' ? (
                            <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0">CRITIC</Badge>
                          ) : (
                            <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">ATENTIE</Badge>
                          )}
                          <span className="text-sm font-medium truncate">{alert.article_name}</span>
                          <Badge variant="outline" className="text-[10px] font-mono">{alert.article_code}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span>Deficit: <strong className="text-red-600">{alert.deficit} {alert.unit}</strong></span>
                          {alert.best_supplier && (
                            <span>Furnizor: <strong>{alert.best_supplier.supplier_name}</strong></span>
                          )}
                          {alert.best_supplier?.price > 0 && (
                            <span className="font-mono">{alert.best_supplier.price.toFixed(2)} RON/{alert.unit}</span>
                          )}
                        </div>
                      </div>
                      {alert.best_supplier?.estimated_cost > 0 && (
                        <span className="text-sm font-bold font-mono text-emerald-700 shrink-0">{alert.best_supplier.estimated_cost.toFixed(2)} RON</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Total */}
            {orderSelectedIds.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm text-slate-600">Cost total estimat:</span>
                <span className="text-lg font-black font-mono text-emerald-700">
                  {orderSelectedIds.reduce((sum, idx) => {
                    const alert = smartAlerts?.alerts?.[idx];
                    return sum + (alert?.best_supplier?.estimated_cost || 0);
                  }, 0).toLocaleString('ro-RO', { minimumFractionDigits: 2 })} RON
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>Anuleaza</Button>
            <Button onClick={submitOrder} disabled={orderSubmitting || orderSelectedIds.length === 0 || !orderLocationId}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-order-btn">
              {orderSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Creeaza Comanda ({orderSelectedIds.length} articole)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierIntelligencePage;
